/**
 * Utility functions for the home plugin
 */
import cloudidIcon from './assets/tool-icons/cloudid.png';
import spaceliftIcon from './assets/tool-icons/spacelift.png';
import splunkIcon from './assets/tool-icons/splunk.svg';

export const sectionCardStyle = {
  minHeight: 'clamp(150px, 22vh, 210px)',
  height: '100%',
  position: 'relative' as const,
  display: 'flex',
  flexDirection: 'column' as const,
  boxSizing: 'border-box' as const,
  fontSize: '0.875rem',
  lineHeight: 1.5,
};

export const sectionGridItemStyle = {
  display: 'flex',
};

export const sectionCardHeaderStyle = {
  padding: '8px 12px',
  minHeight: '40px',
  borderBottom: '1px solid var(--bui-border-default)',
  boxSizing: 'border-box' as const,
};

export const sectionCardBodyStyle = {
  padding: '6px 12px 10px',
  flex: '1 1 auto',
  boxSizing: 'border-box' as const,
};

export const sectionHeadingVariant = 'title-x-small' as const;
export const detailTextVariant = 'body-small' as const;
export const LAST_VISITED_MAX_ITEMS = 4;

export const headerStyle = {
  backgroundColor: '#002169',
  backgroundImage: 'linear-gradient(90deg, #002169 0%, #002169 100%)',
  color: '#ffffff',
};

export const pageStyle = {
  minHeight: '100%',
};

export const linkIconStyle = {
  color: 'var(--bui-fg-announcement)',
};

export const smallTextStyle = {
  fontSize: '0.75rem',
  lineHeight: 1.4,
  whiteSpace: 'nowrap' as const,
};

export const whiteTextStyle = {
  color: '#ffffff',
};

export const borderBottomStyle = {
  borderBottom: '1px solid var(--bui-border-default)',
};

export const announcementsGridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr auto auto',
  columnGap: '12px',
};

// Link renders inline-block by default, which collapses the row to its content
// width and misaligns grid columns from the header. Make it fill the row.
export const fullWidthLinkStyle = {
  display: 'block' as const,
  width: '100%',
};

export const minWidth80Style = {
  minWidth: '80px',
};

export const noWrapStyle = {
  whiteSpace: 'nowrap' as const,
};

export const minWidth80NoWrapStyle = {
  ...minWidth80Style,
  ...noWrapStyle,
};

export const ellipsisNoWrapStyle = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
};

export const lastVisitedGridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr auto auto',
  alignItems: 'baseline',
  columnGap: '12px',
  justifyContent: 'start',
};

export const chevronIconStyle = {
  color: 'var(--bui-fg-announcement)',
};

/**
 * Format a Unix timestamp or ISO date string to human-readable date/time.
 * Returns undefined for missing or unparseable values.
 */
export function formatWhen(
  value: number | string | undefined,
): string | undefined {
  if (value === undefined || value === '') {
    return undefined;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/**
 * Normalize a catalog path for comparison
 */
export function normalizeCatalogPath(path: string): string {
  return path.split('?')[0].replace(/\/+$/, '').toLowerCase();
}

/**
 * Get a user-friendly label for the visited link type
 */
export function visitedTypeLabel(path: string): string {
  const segments = path.split('?')[0].split('/').filter(Boolean);

  if (segments[0] === 'catalog' && segments.length >= 4) {
    const kind = segments[2];
    return `${kind.charAt(0).toUpperCase()}${kind.slice(1)}`;
  }

  if (segments[0] === 'docs') {
    return 'Docs';
  }

  if (segments[0] === 'settings') {
    return 'Settings';
  }

  if (segments[0] === 'home') {
    return 'Home';
  }

  return 'Page';
}

/**
 * Get brand icon configuration for a given icon name
 */
export function getBrandIconConfig(
  icon: string | undefined,
): { src: string; alt: string } | string | null {
  if (!icon) {
    return null;
  }

  if (icon === 'blokk') {
    return '🏦';
  }

  const brandIcons: Record<string, { src: string; alt: string }> = {
    cloudid: {
      src: cloudidIcon,
      alt: 'CloudID',
    },
    spacelift: {
      src: spaceliftIcon,
      alt: 'Spacelift',
    },
    splunk: {
      src: splunkIcon,
      alt: 'Splunk',
    },
  };

  const brandIcon = brandIcons[icon];
  if (!brandIcon) {
    return '🔗';
  }

  return { src: brandIcon.src, alt: brandIcon.alt };
}
